import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { StartupProfileData } from '@/features/profiles/api/startup-profile.api';
import { useEntityProfile } from '@/features/profiles/hooks/useEntityProfile';
import type { ActiveMembership } from '@/lib/types/auth.types';
import { DATA_ROOM_STRINGS } from '../constants';
import { DataRoomRefusal } from './DataRoomRefusal';
import { IssueReportExplainer } from './IssueReportExplainer';

export interface DataRoomNoSeatProps {
  /** The acting startup membership. */
  membership: ActiveMembership;
}

/**
 * The Data Room page when the startup has access but the caller holds no
 * seat (`seat_not_assigned`). What that means depends on the service, which
 * the startup's SR NEXT flag tells apart:
 *
 * - SR NEXT: the second Premium seat can be given to them, so the refusal
 *   sends them to the Startup Admin who assigns it (FR-139).
 * - Anyone else: the startup holds an Issue Report, whose one seat is its
 *   buyer's and cannot move (FR-138), so "ask for a seat" would be false.
 *   They get the explainer, saying the report is open to its buyer.
 */
export function DataRoomNoSeat({ membership }: DataRoomNoSeatProps) {
  const { data, isLoading } = useEntityProfile(membership);
  if (isLoading) return <LoadingSpinner label={DATA_ROOM_STRINGS.PAGE.LOADING} />;

  const profile = data as StartupProfileData | undefined;
  if (profile && !profile.isSrNextClient) {
    return <IssueReportExplainer startupId={membership.entityId} heldByBuyer />;
  }
  return <DataRoomRefusal reason="seat_not_assigned" fullScreen={false} />;
}
